import CartSummary from '@/components/CartSummary';
import DeliveryDateCard from '@/components/Cart/DeliveryDateCard';
import DeliveryEstimateCardShimmer from '@/components/Cart/DeliveryDateCard/Skeleton';
import EmptyCart from '@/components/EmptyCart';
import GenericErrorScreen from '@/components/ErrorScreen';
import CartPageHeader from '@/components/Headers/CartPageHeader';
import {
	VerticalCartBreakup,
	VerticalCartBreakupShimmer,
} from '@/components/CartBreakup/VerticalCartBreakup';
import { getColor } from '@/utils/theme/colors';
import { getStyle } from '@/utils/theme/fonts';
import { useCart } from '@/hooks/useCart';
import { ProvideFullScreenLoader } from '@/hooks/useFullScreenLoader';
import { ApiState, ViewStates } from '@/models/generics';
import { useRef, useState } from 'react';
import { Box, Container, Skeleton, Checkbox, TextField } from '@mui/material';

import { useEffect } from 'react';
import FlexBox from '@/atomic-components/FlexBox';
import { emitMoeEvent } from '@/utils/dataLayer';
import Footer from '@/components/Footers';
import { Breakup, CART_TYPE } from '@/services/cart/models/cart';
import { useRouter } from 'next/router';
import VerticalCart from '@/components/Cart/VerticalCart';
import { getCartPageSource } from '@/utils/cart';
import CartListShimmer from '@/components/Cart/Shimmer';
import { OOSCartItemsBulkActionDrawer } from '@/components/Cart/OOSCartItemsBulkActionDrawer';
import { LencoImage } from '@/atomic-components/Image';
import {
	WhatsappConsentSelection,
	WhatsappConsentStatusValues,
} from '@/components/WhatsappConsentSelection';
import { useUser } from '@/hooks/useUser';
import { userService } from '@/services/user';
import OffersCart from '@/components/Cart/CartOffers/OffersCart';
import { useLocation } from '@/hooks/useLocation';

// let cartViewedEmited = false;

const CartPage = () => {
	const {
		getCartApiState,
		totalCartItems,
		cartActions,
		buyCart,
		rentCart,
		cart,
		buyCartItems,
		buyCartItemsCount,
		rentalCartItems,
		rentalCartItemsCount,
		getCartAttributes,
	} = useCart();

	const router = useRouter();
	const buyCartBreakupRef = useRef<HTMLElement>(null);
	const rentCartBreakupRef = useRef<HTMLElement>(null);
	const [isGstSelected, setIsGstSelected] = useState<boolean>(false);
	const [gstin, setGstin] = useState<string>('');
	const { user, userActions, addresses, isLoggedIn } = useUser();
	const { selectedLocation } = useLocation();
	const canShowWhatsappConsentSelection = () => !user?.whats_app_opt_in;

	function gstInvoiceCheckoutTapped(_: any) {
		setIsGstSelected(!isGstSelected);
	}

	function onGstInInput(e: any) {
		setGstin(e?.target?.value);
	}
	const [
		oosItemsBulkActionDrawerViewState,
		setOosItemsBulkActionDrawerViewState,
	] = useState<ViewStates>(ViewStates.NOT_VISIBLE);

	const isLoadingCart = getCartApiState == ApiState.LOADING;
	const isNonEmptyCart =
		getCartApiState == ApiState.SUCCESS && totalCartItems > 0;

	const scrollToBuyCartBreakup = () => {
		const headerOffset = 280;
		const elementPosition =
			buyCartBreakupRef?.current?.getBoundingClientRect().top;
		const offsetPosition =
			(elementPosition as number) + window.pageYOffset - headerOffset;
		window.scrollTo({
			top: offsetPosition,
			behavior: 'smooth',
		});
	};

	const [currentWhatsappConsentValue, setCurrentWhatsappConsentValue] =
		useState<WhatsappConsentStatusValues>(
			WhatsappConsentStatusValues.CONSENTED
		);

	const canUpdateWhatsappConsentStatus = () => {
		const actualValue = user?.whats_app_opt_in
			? WhatsappConsentStatusValues.CONSENTED
			: WhatsappConsentStatusValues.NOT_CONSENTED;

		return currentWhatsappConsentValue != actualValue;
	};
	const updateWhatsappConsentStatus = () => {
		if (currentWhatsappConsentValue == WhatsappConsentStatusValues.CONSENTED) {
			emitMoeEvent('Cart Whatsapp Consent Checked', {
				cart_type: getCartAttributes().lineOfProductsString,
				vertical: getCartAttributes().verticalsString,
			});
		} else {
			emitMoeEvent('Cart Whatsapp Consent Dismissed', {
				cart_type: getCartAttributes().lineOfProductsString,
				vertical: getCartAttributes().verticalsString,
			});
		}
		if (!canUpdateWhatsappConsentStatus()) return;

		userService
			.updateUserWhatsAppConsentStatus({
				user_consent: {
					opt_in:
						currentWhatsappConsentValue ==
						WhatsappConsentStatusValues.CONSENTED,
				},
			})
			.then((res) => {
				userActions.getUser();
			});
	};

	const scrollToRentCartBreakup = () => {
		const headerOffset = 300;
		const elementPosition =
			rentCartBreakupRef?.current?.getBoundingClientRect().top;
		const offsetPosition =
			(elementPosition as number) + window.pageYOffset - headerOffset;
		window.scrollTo({
			top: offsetPosition,
			behavior: 'smooth',
		});
	};

	const scrollToCartBreakup = (cartType: CART_TYPE) => {
		if (cartType == CART_TYPE.BUY) {
			scrollToBuyCartBreakup();
		} else {
			scrollToRentCartBreakup();
		}
	};

	const displayDeliveryEstimateComponentWithShimmer = () => {
		if (getCartApiState === ApiState.LOADING) {
			return (
				<Box mt='24px' mb='24px'>
					<DeliveryEstimateCardShimmer />
				</Box>
			);
		}

		return (
			<Box>
				<Box mb={2}>
					<DeliveryDateCard
						isDeliveryAddressAvailable={
							cart?.shippingAddressDetails?.id &&
							cart?.shippingAddressDetails?.pincode == selectedLocation.pincode
						}
						deliveryAddress={cart?.shippingAddressDetails}
						verticals={['FURLENCO_SALE', 'FURLENCO_RENTAL']}
					/>
				</Box>
			</Box>
		);
	};

	const displayCartsBreakupWithShimmer = () => {
		if (getCartApiState == ApiState.LOADING) {
			return (
				<Box mt='24px'>
					<VerticalCartBreakupShimmer />
				</Box>
			);
		}

		return (
			<Box mt='24px'>
				{rentCart && (rentCart?.rentalCartItemsCount as number) > 0 && (
					<Box mt='24px' ref={rentCartBreakupRef}>
						<VerticalCartBreakup
							context='cart'
							titleDetails={
								rentCart.summary?.breakupDetailedTitleHeader as Breakup
							}
							cartItemsDetails={rentCart.summary}
						/>
					</Box>
				)}

				{buyCart && (buyCart?.buyCartItemsCount as number) > 0 && (
					<Box mt='24px' ref={buyCartBreakupRef}>
						<VerticalCartBreakup
							context='cart'
							titleDetails={buyCart.summary?.breakupDetailedTitleHeader}
							cartItemsDetails={buyCart.summary}
						/>
					</Box>
				)}
			</Box>
		);
	};

	useEffect(() => {
		if (cart?.id) {
			// cartViewedEmited = true;

			const {
				cartStatus,
				buyProductIdsString,
				rentalProductIdsString,
				lineOfProductsString,
			} = getCartAttributes();

			emitMoeEvent('Cart Viewed', {
				total_cart_value: cart?.total?.value,
				buy_cart_value: buyCart?.total?.value,
				rent_cart_value: rentCart?.total?.value,
				rent_products: rentalProductIdsString,
				buy_products: buyProductIdsString,
				cart_status: cartStatus,
				lineofproduct: lineOfProductsString,
				mix_cart: rentCart?.tenureSelector?.selectedTenure ? false : true,
			});
		}
	}, [cart]);

	const displayCartsListWithShimmer = () => {
		if (getCartApiState === ApiState.LOADING) {
			return <CartListShimmer />;
		}

		return (
			<Box className={getCartPageSource(router)}>
				{buyCartItemsCount > 0 && (
					<VerticalCart
						cartItemsCount={buyCartItemsCount}
						cartType={CART_TYPE.BUY}
						cartItems={buyCartItems}
						verticalCart={buyCart}
						openCartOosActionDrawer={openOosCartItemBulkActionDrawer}
					/>
				)}
				{rentalCartItemsCount > 0 && (
					<VerticalCart
						cartItemsCount={rentalCartItemsCount}
						cartType={CART_TYPE.RENT}
						cartItems={rentalCartItems}
						verticalCart={rentCart}
						openCartOosActionDrawer={openOosCartItemBulkActionDrawer}
						screen='CART'
					/>
				)}
			</Box>
		);
	};

	const canDisplayEmptyCart = () => {
		return (
			totalCartItems == 0 &&
			[ApiState.COMPLETED, ApiState.SUCCESS].includes(getCartApiState)
		);
	};

	const openOosCartItemBulkActionDrawer = () => {
		setOosItemsBulkActionDrawerViewState(ViewStates.VISIBLE);
	};

	const closeOosCartItemBulkActionDrawer = () => {
		setOosItemsBulkActionDrawerViewState(ViewStates.NOT_VISIBLE);
	};

	const doesCartContainsOosItems = () => {
		const containsOosItems =
			buyCartItems?.some((item) => item.isOutOfStock) ||
			rentalCartItems?.some((item) => item.isOutOfStock);

		return containsOosItems;
	};

	useEffect(() => {
		if (
			oosItemsBulkActionDrawerViewState == ViewStates.VISIBLE &&
			!doesCartContainsOosItems()
		) {
			setOosItemsBulkActionDrawerViewState(ViewStates.NOT_VISIBLE);
		}
	}, [cart]);

	useEffect(() => {
		userActions.getAddresses(() => {});
	}, [selectedLocation]);

	const getDefaultShippingAddress = async () => {
		await cartActions.getCart({
			withDefaultAddress: true,
		});
	};

	// useEffect(() => {
	// 	if (
	// 		cart?.id &&
	// 		!cart?.shippingAddressDetails &&
	// 		!hasSentWithDefaultAddress.current
	// 	) {
	// 		getDefaultShippingAddress();
	// 		hasSentWithDefaultAddress.current = true;
	// 	}
	// }, [cart]);

	useEffect(() => {
		if (isLoggedIn) {
			getDefaultShippingAddress();
			// userActions.getAddresses(() => {});
		} else {
			cartActions.getCart();
		}
	}, [selectedLocation.pincode, isLoggedIn]);

	if (getCartApiState == ApiState.FAILED) {
		return <GenericErrorScreen />;
	}

	// const displayGstSelectionCheckbox = () => {
	// 	if (getCartApiState == ApiState.LOADING) return null;

	// 	return (
	// 		<Box p='24px 0px'>
	// 			<Box
	// 				p='24px 32px'
	// 				sx={{ border: '1px solid #E8E8E8', borderRadius: '8px' }}
	// 			>
	// 				<FlexBox justifyContent='start' alignItems={'center'}>
	// 					<Checkbox
	// 						checked={isGstSelected}
	// 						onChange={gstInvoiceCheckoutTapped}
	// 						sx={{ p: '0px', mr: '8px' }}
	// 					/>
	// 					<span
	// 						style={{
	// 							color: getColor('Gray -2'),
	// 							...getStyle('Small_Medium'),
	// 						}}
	// 						className='cursor-pointer'
	// 						onClick={gstInvoiceCheckoutTapped}
	// 					>
	// 						Use GST invoice
	// 					</span>
	// 				</FlexBox>
	// 				{isGstSelected && (
	// 					<>
	// 						<Box
	// 							style={{
	// 								color: getColor('Gray -2'),
	// 								marginBottom: '8px',
	// 								...getStyle('Small_Medium'),
	// 							}}
	// 							mt='24px'
	// 						>
	// 							GSTIN
	// 						</Box>
	// 						<TextField
	// 							value={gstin}
	// 							placeholder='Enter GSTIN'
	// 							onChange={onGstInInput}
	// 							maxRows={1}
	// 							sx={{
	// 								width: '100%',
	// 								backgroundColor: getColor('Gray -6'),
	// 							}}
	// 							name='gstin'
	// 							autoComplete='on'
	// 							InputLabelProps={{
	// 								sx: { ...getStyle('Small_Mediun') },
	// 							}}
	// 							InputProps={{
	// 								disableUnderline: true,
	// 							}}
	// 							inputProps={{
	// 								minLength: 15,
	// 								maxLength: 15,
	// 							}}
	// 						></TextField>
	// 					</>
	// 				)}
	// 			</Box>
	// 		</Box>
	// 	);
	// };

	return (
		<ProvideFullScreenLoader>
			<OOSCartItemsBulkActionDrawer
				open={oosItemsBulkActionDrawerViewState == ViewStates.VISIBLE}
				onClose={closeOosCartItemBulkActionDrawer}
			/>

			

			<Box className='CART_PAGE'>
				{canDisplayEmptyCart() && (
					<FlexBox justifyContent='center'>
						<Box width='666px'>
							<EmptyCart />
						</Box>
					</FlexBox>
				)}

				{(isLoadingCart || isNonEmptyCart) && (
					<>
					
					<FlexBox id='cart-main-content' fullWidth>
						<Box width='calc(100% - 437px)' mr='24px'>
						<Box>
					{(isLoadingCart || isNonEmptyCart) && (
						<FlexBox
							position='relative'
							justifyContent='start'
							alignItems='center'
						>
							<Box width='calc(100%)' height='auto'>
								{displayDeliveryEstimateComponentWithShimmer()}
							</Box>
							
						</FlexBox>
					)}
				</Box>
							{displayCartsListWithShimmer()}
						</Box>

						<Box id='cart-side-content' width='100%' sx={{ maxWidth: '413px' }}>
							<Box sx={{ top: '104px' }}>
								<Box sx={{ position: 'sticky', top: '104px', zIndex: 2 }}>
									<CartSummary
										gstin={gstin}
										openCartOosActionDrawer={openOosCartItemBulkActionDrawer}
										closeCartOosActionDrawer={closeOosCartItemBulkActionDrawer}
										onInfoClicked={scrollToCartBreakup}
										updateWhatsappConsentStatus={updateWhatsappConsentStatus}
									/>
								</Box>

								<Box mt='24px'>
									{getCartApiState != ApiState.LOADING &&
										(isLoadingCart || isNonEmptyCart) && <OffersCart />}
								</Box>

								{displayCartsBreakupWithShimmer()}
							</Box>
							{/* {displayGstSelectionCheckbox()} */}
						</Box>
					</FlexBox>
					</>
				)}
				
			</Box>
		</ProvideFullScreenLoader>
	);
};

export default CartPage;

CartPage.getLayout = function getLayout(page: any) {
	return (
		<>
			<CartPageHeader />
			<Container maxWidth='lg' sx={{ margin: '38px auto' }}>
				{page}
			</Container>
			<Footer />
		</>
	);
};
